import { useI18n } from '@affine/i18n';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import { cn } from '../../utils';
import { Button } from '../button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../command';
import Input from '../input';

export type PhoneInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'ref'
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void;
    label?: string;
    error?: string;
  };

const PhoneInputContext = createContext<{ hasError: boolean }>({
  hasError: false,
});

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ label, className, onChange, value, error, ...props }, ref) => {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label className="text-sm font-semibold text-secondary">{label}</label>
      )}
      <PhoneInputContext.Provider value={{ hasError: Boolean(error) }}>
        <RPNInput.default
          ref={ref}
          className="flex"
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          inputComponent={InputComponent}
          smartCaret={false}
          value={value || undefined}
          /**
           * Handles the onChange event.
           *
           * react-phone-number-input might trigger the onChange event as undefined
           * when a valid phone number is not entered. To prevent this,
           * the value is coerced to an empty string.
           *
           * @param {E164Number | undefined} value - The entered value
           */
          onChange={value => onChange?.(value || ('' as RPNInput.Value))}
          {...props}
        />
      </PhoneInputContext.Provider>
      {error ? (
        <span className="mt-1 text-xs text-destructive">{error}</span>
      ) : null}
    </div>
  );
});
PhoneInput.displayName = 'PhoneInput';

const InputComponent = forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ onChange, value, placeholder }, ref) => {
  const { hasError } = useContext(PhoneInputContext);
  return (
    <Input
      className={'!rounded-l-none'}
      size="extraLarge"
      value={value}
      onChange={(newValue: string) => {
        onChange?.({
          target: { value: newValue },
        } as React.ChangeEvent<HTMLInputElement>);
        return;
      }}
      status={hasError ? 'error' : 'default'}
      placeholder={placeholder}
      ref={ref}
    />
  );
});
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

const NO_SELECTION_VALUE = '__no-selection__' as const;

const buildCommandItemValue = (country: RPNInput.Country, label: string) => {
  const callingCode = RPNInput.getCountryCallingCode(country);
  return `${country} ${label} +${callingCode}`.toLowerCase();
};

const filter = (value: string, search: string): number => {
  if (!search) return 1;
  return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
};

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const t = useI18n();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selectedCountryOption = useMemo(
    () => countryList.find(country => country.value === selectedCountry),
    [countryList, selectedCountry]
  );
  const selectedCountryCommandValue = selectedCountryOption?.value
    ? buildCommandItemValue(
        selectedCountryOption.value,
        selectedCountryOption.label
      )
    : NO_SELECTION_VALUE;
  const [activeCommandValue, setActiveCommandValue] = useState<string>(
    selectedCountryCommandValue
  );

  const scrollToTop = () => {
    const scrollAreaContent = scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    );
    if (scrollAreaContent) scrollAreaContent.scrollTo?.(0, 0);
  };

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={open => {
        setIsOpen(open);
        open && setSearchValue('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          className={'!border-r-0 !rounded-r-none'}
          disabled={disabled}
          prefix={
            <FlagComponent
              country={selectedCountry}
              countryName={selectedCountry}
            />
          }
          suffix={
            <ChevronsUpDown
              className={cn(
                '-mr-2 size-4 opacity-50',
                disabled ? 'hidden' : 'opacity-100'
              )}
            />
          }
          size="extraLarge"
        ></Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
        <Command
          value={activeCommandValue}
          onValueChange={value =>
            setActiveCommandValue(value ?? NO_SELECTION_VALUE)
          }
          filter={filter}
        >
          <CommandInput
            value={searchValue}
            onValueChange={value => {
              setSearchValue(value);
              requestAnimationFrame(scrollToTop);
              setActiveCommandValue(
                value ? NO_SELECTION_VALUE : selectedCountryCommandValue
              );
            }}
            placeholder={t[
              'docnboard.phone-input.search-country.placeholder'
            ]()}
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>
                {t['docnboard.phone-input.no-country-found']()}
              </CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  ) : null
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };
  const commandItemValue = buildCommandItemValue(country, countryName);

  return (
    <CommandItem
      value={commandItemValue}
      className="gap-2 border-(--input)"
      onSelect={handleSelect}
    >
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-muted [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

const ScrollArea = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn('relative overflow-hidden', className)}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport className="size-full rounded-[inherit]">
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-px',
      orientation === 'horizontal' &&
        'h-2.5 flex-col border-t border-t-transparent p-px',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { PhoneInput };
